import { Button } from 'components/common'
import { DownloadIcon } from 'components/icons'
import { useQuery } from 'hooks'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { exportStockOutReport } from 'utils/api/stockOutReportApi'
import { handleError } from 'utils/helpers/handleError'
import { FILTER_FIELD_STOCK_OUT_REPORT } from './utils'

// How long the button stays disabled after a successful trigger. Only guards
// against double-clicks — the job itself runs far longer than this.
const RETRIGGER_COOLDOWN_MS = 5000

/**
 * Queues a CSV export of the stock-out report, filtered exactly as the table on
 * screen is. The export is asynchronous: the request only accepts the job and
 * the file arrives by email, so this never downloads anything directly.
 */
const StockOutReportExportButton = () => {
  const [disabled, setDisabled] = useState<boolean>(false)

  const { getQuery } = useQuery()
  const cooldownTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    return () => clearTimeout(cooldownTimer.current)
  }, [])

  const handleExport = async () => {
    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      // Unfiltered is allowed — the server strips empty values, so an untouched
      // filter bar simply exports everything.
      await exportStockOutReport(getQuery(FILTER_FIELD_STOCK_OUT_REPORT) || {})

      toast.success(
        "Export started — you'll receive an email with the download link.",
        { id: loadingToast, duration: 5000 },
      )

      setDisabled(true)
      cooldownTimer.current = setTimeout(
        () => setDisabled(false),
        RETRIGGER_COOLDOWN_MS,
      )
    } catch (error) {
      // handleError already surfaces `errors.msg.runningService` verbatim, which
      // is the 422 raised when another export is still in flight.
      handleError(error, loadingToast, {
        default: 'Failed to start the export.',
      })
    }
  }

  return (
    <Button
      type="button"
      buttonText="Export"
      icon={<DownloadIcon />}
      className="px-3.5 py-2 text-sm"
      disabled={disabled}
      onClick={handleExport}
    />
  )
}

export default StockOutReportExportButton
